// Resolves the customer-facing active price for a Service row.
//
// Single source of truth for the "is this a real discount?" question
// every public surface (marketplace card, salon detail page, booking
// page) used to answer inline with `discountPrice ?? price`. That
// naïve fallback honoured ANY non-null `discountPrice` — including
// values >= the regular price, which a salon owner can enter by
// accident in the services modal. The result was the inconsistency
// the SalonServicesPage owner view dodged with its own
// `discountPrice < price` guard but the public surfaces didn't.
//
// Contract: a discount is "active" only when it is a positive,
// finite number strictly below the standard price. Anything else
// (null, undefined, NaN, negative, or >= price) yields the standard
// price. Callers can pass either a Service (Prisma shape) or any
// object that exposes `{ price, discountPrice }` — both fields are
// coerced through Number() so string-typed API responses work too.
//
// Returns 0 when the row has no usable price at all, mirroring the
// previous `?? 0` final fallback.
export function resolveActivePrice(service) {
  const price = Number(service?.price ?? 0);
  if (!Number.isFinite(price) || price < 0) return 0;
  const raw = service?.discountPrice;
  if (raw === null || raw === undefined || raw === '') return price;
  const discount = Number(raw);
  if (!Number.isFinite(discount) || discount < 0 || discount >= price) return price;
  return discount;
}

// True when `discountPrice` is a real discount the public surface
// should advertise (e.g. struck-through-original UI). Derived from
// resolveActivePrice so the "what counts as a discount" contract
// lives in one place — if the helper changes its mind about a value
// (negative, NaN, empty string, equal-to-price), this function follows
// automatically without a parallel set of conditionals to keep in sync.
export function hasActiveDiscount(service) {
  const originalPrice = Number(service?.price ?? NaN);
  if (!isPriceKnown(originalPrice)) return false;
  return resolveActivePrice(service) < originalPrice;
}

// True when `price` is a positive finite number we can display to the
// user. The full predicate (`Number.isFinite(p) && p > 0`) had crept
// inline into 5 sites: hasActiveDiscount above, SalonDetailPage's
// startingPrice memo + service-card render gate,
// ServiceBookingDrawer's hasUnknownPrice memo + line-item gate. Same
// rule each time — "this price isn't 0, NaN, Infinity, or negative,
// so we can put RON next to it." Centralising means a future tweak
// (e.g. require >= some MIN_PRICE) lands in one place.
//
// Input runs through Number() so the helper accepts a raw API field
// (e.g. a numeric string from a JSON boundary that didn't coerce)
// the same way it accepts an already-parsed Number — matches
// resolveActivePrice / hasActiveDiscount above, which both coerce
// internally. Current callsites all pre-coerce so the second Number()
// is a no-op for them, but the helper is now safe to call with the
// raw `service.price` directly.
//
// Not used for the tier-breakdown price/duration pair in
// SalonDetailPage — those two checks sit adjacent (price then
// duration) and read better inline with the same shape than mixing
// helper-for-price + inline-for-duration. Calendar.js's totalMin
// check is a different code path (event-duration math, not display)
// and stays inline too.
export function isPriceKnown(price) {
  const p = Number(price);
  return Number.isFinite(p) && p > 0;
}

// ─── Size-aware pricing ──────────────────────────────────────────────
// A BY_SIZE service prices per pet size via `priceTiers` (one row per
// PetSize); booking creation snapshots the tier matching the pet's size
// onto the BookingItem. FLAT services — or a missing/unserved size with no
// matching tier — fall back to the service's own active price + duration.
//
// Lifted out of ServiceBookingDrawer (which asked for exactly this in a
// "future cleanup" note) so the client booking drawer and the owner
// settlement drawer resolve a BY_SIZE service's price for a given pet size
// through one shared rule instead of two copies that could drift.
export function resolveTier(service, petSize) {
  if (!service || service.pricingMode !== 'BY_SIZE' || !petSize) return null;
  if (!Array.isArray(service.priceTiers)) return null;
  return service.priceTiers.find((t) => t.petSize === petSize) || null;
}

export function resolvePrice(service, petSize) {
  const tier = resolveTier(service, petSize);
  if (tier) return Number(tier.price);
  return resolveActivePrice(service);
}

export function resolveDuration(service, petSize) {
  const tier = resolveTier(service, petSize);
  if (tier) return Number(tier.duration);
  return Number(service?.duration || 0);
}
